import EstimateProvider from "@/providers/estimate.provider";
import { clearRequestsCache } from "@/providers/http.provider";
import type { EstimateDetail } from "@/interfaces/estimate-detail";
import { estimationActions } from "@/redux/actions/estimation.actions";
import type { AppDispatch } from "@/redux/store";
import { getLineItemComponentKey } from "@/util/utils";

/**
 * Fetches get_final_estimates + xactimate mapping and writes the result into Redux (same as searchEstimateDetail).
 * Line list UI reads estimationDetail from Redux, not TanStack Query — updates to metadata alone do not refresh lines.
 */
export async function syncEstimateDetailFromServerToRedux(
  dispatch: AppDispatch,
  estimateId: string,
  options?: { silent?: boolean },
): Promise<EstimateDetail[] | undefined> {
  if (!options?.silent) {
    dispatch(estimationActions.setEstimationState({ isLoading: true }));
  }

  clearRequestsCache();

  try {
    const [estimateResponse, xactimateResponse] = await Promise.all([
      EstimateProvider.getEstimateDetail(estimateId),
      EstimateProvider.getXactimateMapping(estimateId),
    ]);

    if (!estimateResponse.success) {
      console.error("Error getting estimate detail");
    }

    const rooms = estimateResponse.data ?? [];
    const _knownLineItemComponentKeys = rooms.flatMap((room) =>
      room.dataForUI.page.tuplesOutput.flatMap((tuple) =>
        tuple.lineItems.map((lineItem) => getLineItemComponentKey(room.dataForUI.metadata.pageId, tuple, lineItem)),
      ),
    );

    dispatch(
      estimationActions.setEstimationState({
        estimationDetail: rooms,
        exportedToXactimate: estimateResponse.exportedToXactimate,
        lineItemXactimateMapping: xactimateResponse.data || [],
        isLoading: false,
        files: estimateResponse.files ?? [],
        knownLineItemComponentKeys: _knownLineItemComponentKeys,
      }),
    );

    return rooms;
  } catch {
    dispatch(
      estimationActions.setEstimationState({
        isLoading: false,
      }),
    );
    return undefined;
  }
}
